(() => {
  if (customElements.get('athos-autocomplete')) return;

  const DEBOUNCE_MS = 220;
  const CARD_TIMEOUT_MS = 6000;

  const debounce = (fn, wait) => {
    let timer;
    return () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(fn, wait);
    };
  };

  const bannerMarkup = (value) => {
    const template = document.createElement('template');
    template.innerHTML = String(value || '');
    template.content.querySelectorAll('script').forEach((node) => node.remove());
    return template.innerHTML.trim();
  };

  class AthosAutocomplete extends HTMLElement {
    connectedCallback() {
      if (this.dataset.wired) return;
      if (!window.AthosClient || !window.AthosCards) return;
      if (!this.dataset.siteId) {
        console.error('[athos-autocomplete] Missing Athos site ID. Set it in Theme settings → Athos / Searchspring → “Athos site ID”.');
        return;
      }

      this.input = this.querySelector('[data-athos-ac-input]');
      this.flyout = this.querySelector('[data-athos-ac-flyout]');
      if (!this.input || !this.flyout) return;

      this.dataset.wired = 'true';
      this.clearButton = this.querySelector('[data-athos-ac-clear]');
      this.termsEl = this.querySelector('[data-athos-ac-terms]');
      this.productsEl = this.querySelector('[data-athos-ac-products]');
      this.seeAllEl = this.querySelector('[data-athos-ac-see-all]');
      this.emptyEl = this.querySelector('[data-athos-ac-empty]');
      this.emptyTextEl = this.querySelector('[data-athos-ac-empty-text]');
      this.statusEl = this.querySelector('[data-athos-ac-status]');

      this.minChars = Number.parseInt(this.dataset.minChars, 10) || 2;
      this.productLimit = Number.parseInt(this.dataset.productLimit, 10) || 6;
      this.suggestionLimit = Number.parseInt(this.dataset.suggestionLimit, 10) || 6;
      this.showBanners = this.dataset.showBanners !== 'false';
      this.sort = window.AthosClient.parseSortValue(this.dataset.defaultSort);
      this.searchUrl = this.dataset.searchUrl || '/search';
      this.hasRendered = false;

      this.client = window.AthosClient.createClient({ siteId: this.dataset.siteId });
      this.runQuery = debounce(() => this.onQuery(), DEBOUNCE_MS);

      this.bindEvents();
      this.toggleClear();
    }

    disconnectedCallback() {
      this.requestController?.abort();
      document.removeEventListener('pointerdown', this.onDocumentPointerDown);
    }

    bindEvents() {
      this.input.addEventListener('input', () => {
        this.dismissed = false;
        this.toggleClear();
        this.runQuery();
      });
      this.input.addEventListener('focus', () => {
        if (!this.dismissed && this.currentQuery().length >= this.minChars && this.hasRendered) this.open();
      });
      this.input.addEventListener('keydown', (event) => this.onInputKeydown(event));
      this.clearButton?.addEventListener('click', () => this.clear());
      this.flyout.addEventListener('keydown', (event) => this.onFlyoutKeydown(event));
      this.addEventListener('focusout', (event) => this.onFocusOut(event));

      this.onDocumentPointerDown = (event) => {
        if (this.flyout.hidden || this.contains(event.target)) return;
        this.close();
      };
      document.addEventListener('pointerdown', this.onDocumentPointerDown);
    }

    currentQuery() {
      return this.input.value.trim();
    }

    toggleClear() {
      if (this.clearButton) this.clearButton.hidden = this.currentQuery().length === 0;
    }

    async onQuery() {
      this.requestController?.abort();

      const query = this.currentQuery();
      if (query.length < this.minChars) {
        this.hasRendered = false;
        this.close();
        return;
      }

      this.requestController = new AbortController();
      const signal = this.requestController.signal;

      const [terms, data] = await Promise.all([
        this.client.fetchSuggest(query, { signal, limit: this.suggestionLimit }).catch(() => []),
        this.client.fetchAutocomplete({ searchTerms: query, resultsPerPage: this.productLimit, sort: this.sort }, { signal }).catch(() => undefined),
      ]);
      if (signal.aborted || this.currentQuery() !== query) return;
      if (!this.hasFocus()) return this.discardPendingResults();

      this.renderTerms(terms, query);
      await this.renderProducts(data, signal);
      if (signal.aborted || this.currentQuery() !== query) return;
      if (!this.hasFocus()) return this.discardPendingResults();

      this.updateSeeAll(query, data, terms);
      this.updateEmpty(query, terms, data);
      this.announce(query, terms, data);
      this.hasRendered = true;
      if (!this.dismissed) this.open();
    }

    hasFocus() {
      return this.contains(document.activeElement);
    }

    discardPendingResults() {
      this.hasRendered = false;
    }

    announce(query, terms, data) {
      if (!this.statusEl) return;
      const products = Array.isArray(data?.results) ? data.results.length : 0;
      const total = (terms || []).length + products;
      if (!total) {
        const template = this.dataset.noResultsLabel || 'No results found for “[[q]]”';
        this.statusEl.textContent = template.replace('[[q]]', query);
        return;
      }
      const template = this.dataset.resultsLabel || '[[n]] results';
      this.statusEl.textContent = template.replace('[[n]]', String(total));
    }

    renderTerms(terms, query) {
      if (!this.termsEl) return;
      this.termsEl.replaceChildren();
      const list = (terms || []).slice(0, this.suggestionLimit);
      if (!list.length) {
        this.termsEl.hidden = true;
        return;
      }

      const heading = document.createElement('p');
      heading.className = 'athos-ac__heading';
      heading.textContent = this.dataset.termsLabel || 'Suggestions';

      const ul = document.createElement('ul');
      ul.className = 'athos-ac__terms-list';
      list.forEach((term) => {
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.className = 'athos-ac__term-link';
        link.href = this.searchHref(term);
        link.textContent = term;
        item.appendChild(link);
        ul.appendChild(item);
      });

      this.termsEl.append(heading, ul);
      this.termsEl.hidden = false;
    }

    async renderProducts(data, signal) {
      if (!this.productsEl) return;
      this.productsEl.replaceChildren();

      const results = Array.isArray(data?.results) ? data.results : [];
      const handles = results
        .map((result) => window.AthosClient.handleFromResult(result))
        .filter(Boolean)
        .slice(0, this.productLimit);
      if (!handles.length) {
        this.productsEl.hidden = true;
        return;
      }

      const cards = await window.AthosCards.fetchCards(handles, { signal, timeoutMs: CARD_TIMEOUT_MS, view: 'card-search' });
      if (signal.aborted) return;

      const grid = document.createElement('div');
      grid.className = 'athos-ac__grid';
      cards.forEach((html) => {
        if (html) grid.insertAdjacentHTML('beforeend', html);
      });
      if (!grid.childElementCount) {
        this.productsEl.hidden = true;
        return;
      }

      this.injectBanners(grid, data);

      const heading = document.createElement('p');
      heading.className = 'athos-ac__heading';
      heading.textContent = this.dataset.productsLabel || 'Products';

      this.productsEl.append(heading, grid);
      this.productsEl.hidden = false;
      window.DisplayGroupSwatches?.register(grid);
    }

    injectBanners(grid, data) {
      if (!this.showBanners) return;
      const banners = data?.merchandising?.content?.inline;
      if (!Array.isArray(banners) || !banners.length) return;

      banners
        .slice()
        .sort((a, b) => (a?.config?.position?.index ?? 0) - (b?.config?.position?.index ?? 0))
        .forEach((banner) => {
          const markup = bannerMarkup(banner?.value);
          if (!markup) return;
          const cell = document.createElement('div');
          cell.className = 'athos-ac__banner';
          cell.setAttribute('role', 'presentation');
          cell.innerHTML = markup;
          const index = Number.parseInt(banner?.config?.position?.index, 10);
          const ref = Number.isFinite(index) ? grid.children[index] : undefined;
          grid.insertBefore(cell, ref || null);
        });
    }

    updateSeeAll(query, data, terms) {
      if (!this.seeAllEl) return;
      const hasProducts = Array.isArray(data?.results) && data.results.length > 0;
      if (!hasProducts && !(terms || []).length) {
        this.seeAllEl.hidden = true;
        return;
      }

      const total = data?.pagination?.productResults ?? data?.pagination?.totalResults;
      const label = this.dataset.seeAllLabel || 'See all results';
      this.seeAllEl.href = this.searchHref(query);
      this.seeAllEl.textContent = Number.isFinite(Number(total)) ? `${label} (${total})` : label;
      this.seeAllEl.hidden = false;
    }

    updateEmpty(query, terms, data) {
      if (!this.emptyEl) return;
      const hasResults = (terms || []).length > 0 || (Array.isArray(data?.results) && data.results.length > 0);
      if (this.emptyTextEl) {
        const template = this.dataset.noResultsLabel || 'No results found for “[[q]]”';
        this.emptyTextEl.textContent = template.replace('[[q]]', query);
      }
      this.emptyEl.hidden = hasResults;
    }

    searchHref(term) {
      const url = new URL(this.searchUrl, window.location.origin);
      url.searchParams.set('q', term);
      return url.pathname + url.search;
    }

    open() {
      this.flyout.hidden = false;
      this.classList.add('athos-ac--open');
      this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.flyout.hidden = true;
      this.classList.remove('athos-ac--open');
      this.input.setAttribute('aria-expanded', 'false');
    }

    clear() {
      this.input.value = '';
      this.toggleClear();
      this.hasRendered = false;
      this.requestController?.abort();
      this.close();
      this.input.focus();
    }

    flyoutLinks() {
      return Array.from(this.flyout.querySelectorAll('a[href]'))
        .filter((link) => !link.hidden && link.offsetWidth + link.offsetHeight > 0);
    }

    onInputKeydown(event) {
      if (event.key === 'ArrowDown' && !this.flyout.hidden) {
        const links = this.flyoutLinks();
        if (links.length) {
          event.preventDefault();
          links[0].focus();
        }
      } else if (event.key === 'Escape' && !this.flyout.hidden) {
        event.preventDefault();
        this.dismissed = true;
        this.close();
      }
    }

    onFlyoutKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.dismissed = true;
        this.close();
        this.input.focus();
        return;
      }
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

      const links = this.flyoutLinks();
      const index = links.indexOf(document.activeElement);
      event.preventDefault();
      if (event.key === 'ArrowDown') {
        links[index + 1]?.focus();
      } else if (index <= 0) {
        this.input.focus();
      } else {
        links[index - 1].focus();
      }
    }

    onFocusOut(event) {
      if (!event.relatedTarget || this.contains(event.relatedTarget)) return;
      this.requestController?.abort();
      this.close();
    }
  }

  customElements.define('athos-autocomplete', AthosAutocomplete);
})();
